import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { timesheetAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ChevronLeft, ChevronRight, Plus, Send, Clock,
  Calendar, Loader2, CheckCircle, XCircle, Edit, Download
} from 'lucide-react';
import { TimesheetWeeklyGrid } from '../components/timesheet/TimesheetWeeklyGrid';
import { TimesheetEntryDialog } from '../components/timesheet/TimesheetEntryDialog';
import { TimesheetAISummary } from '../components/timesheet/TimesheetAISummary';

const STATUS_BADGES = {
  draft: { color: 'bg-gray-100 text-gray-800', icon: Edit },
  submitted: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
};

function getWeekDates(startDate) {
  const start = new Date(startDate + 'T00:00:00');
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
  }
  return dates;
}

function formatDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const INITIAL_ENTRY_FORM = {
  date: '', project_id: '', task_id: '', hours_spent: 0,
  billable: true, work_type: 'dev', description: '',
  source: 'manual', devops_work_item_id: null, devops_project: null, devops_url: null,
};

export default function MyTimesheetPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [weekData, setWeekData] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(null);
  const [activeTab, setActiveTab] = useState('timesheet');
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [entryForm, setEntryForm] = useState(INITIAL_ENTRY_FORM);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [reportText, setReportText] = useState('');
  const [sendingReport, setSendingReport] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [projects, setProjects] = useState([]);

  // Initialize current week
  useEffect(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const monday = new Date(today);
    monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    const year = monday.getFullYear();
    const month = String(monday.getMonth() + 1).padStart(2, '0');
    const day = String(monday.getDate()).padStart(2, '0');
    setCurrentWeekStart(`${year}-${month}-${day}`);
  }, []);

  const fetchWeekData = useCallback(async (skipDevops = false) => {
    if (!currentWeekStart) return;
    setLoading(true);
    try {
      const [weekRes, projRes] = await Promise.all([
        timesheetAPI.getWeek(currentWeekStart, null, skipDevops),
        timesheetAPI.getProjects()
      ]);
      setWeekData(prev => ({
        ...weekRes.data,
        devops_suggestions: skipDevops && prev?.devops_suggestions?.length
          ? prev.devops_suggestions : weekRes.data.devops_suggestions
      }));
      setProjects(projRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart]);

  useEffect(() => { fetchWeekData(); }, [fetchWeekData]);

  const navigateWeek = (delta) => {
    const current = new Date(currentWeekStart + 'T00:00:00');
    current.setDate(current.getDate() + delta);
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    setCurrentWeekStart(`${year}-${month}-${day}`);
  };

  const openAddEntry = (date = null) => {
    setEditingEntry(null);
    setEntryForm({
      ...INITIAL_ENTRY_FORM,
      date: date || (weekData?.week_start || ''),
      project_id: projects[0]?.id || '',
      hours_spent: 1,
    });
    setEntryDialogOpen(true);
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      date: entry.date, project_id: entry.project_id, task_id: entry.task_id,
      hours_spent: entry.hours_spent, billable: entry.billable, work_type: entry.work_type,
      description: entry.description, source: entry.source,
      devops_work_item_id: entry.devops_work_item_id, devops_project: entry.devops_project,
      devops_url: entry.devops_url,
    });
    setEntryDialogOpen(true);
  };

  const selectDevOpsTask = (task) => {
    const projectName = task.project || task.devops_project || 'client-work';
    const projectId = projects.find(p => p.name === projectName)?.id || 'client-work';
    setEntryForm(prev => ({
      ...prev, project_id: projectId, task_id: `devops-${task.id}`,
      source: 'devops_lookup', devops_work_item_id: task.id,
      devops_project: task.devops_project || task.project, devops_url: task.url,
      description: task.title, project_name_display: projectName,
    }));
    setEntryDialogOpen(true);
  };

  const saveEntry = async () => {
    if (!entryForm.project_id || !entryForm.hours_spent) {
      toast.error('Please fill required fields');
      return;
    }
    setSaving(true);
    try {
      if (editingEntry) {
        await timesheetAPI.updateEntry(editingEntry.id, entryForm);
        toast.success('Entry updated');
      } else {
        await timesheetAPI.createEntry(entryForm);
        toast.success('Entry added');
      }
      setEntryDialogOpen(false);
      fetchWeekData(true);
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail || 'Failed to save entry';
      if (status === 409) {
        // Duplicate task — find existing entry and offer to edit it
        const existing = weekData?.entries?.find(
          e => e.date === entryForm.date && e.task_id === entryForm.task_id
        );
        if (existing) {
          toast.warning(detail, { duration: 5000 });
          // Auto-open edit mode for the existing entry
          setEditingEntry(existing);
          setEntryForm(prev => ({ ...prev, hours_spent: existing.hours_spent }));
        } else {
          toast.warning(detail, { duration: 5000 });
        }
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!confirm('Delete this entry?')) return;
    try {
      await timesheetAPI.deleteEntry(entryId);
      toast.success('Entry deleted');
      fetchWeekData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    }
  };

  const submitWeek = async () => {
    if (!confirm('Submit this week for approval? You won\'t be able to edit until it\'s approved or rejected.')) return;
    setSaving(true);
    try {
      await timesheetAPI.submitWeek(currentWeekStart);
      toast.success('Week submitted for approval');
      fetchWeekData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit week');
    } finally {
      setSaving(false);
    }
  };

  const syncFromDevOps = async () => {
    setSyncing(true);
    try {
      const res = await timesheetAPI.syncFromDevOps(currentWeekStart);
      if (res.data.added > 0) {
        toast.success(`Added ${res.data.added} completed tasks from DevOps`);
        fetchWeekData();
      } else if (res.data.skipped > 0) {
        toast.info(`All ${res.data.skipped} tasks already in timesheet`);
      } else {
        toast.info('No completed tasks found for this week');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from DevOps');
    } finally {
      setSyncing(false);
    }
  };

  const generateAISummary = async (mode = 'daily_report') => {
    setAiLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await timesheetAPI.getAISummary({
        mode, date: mode === 'daily_report' ? today : null,
        week_start: mode === 'weekly_insights' ? currentWeekStart : null,
      });
      setAiSummary(res.data);
      setReportText(res.data.daily_report_for_manager || '');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI summary');
    } finally {
      setAiLoading(false);
    }
  };

  const sendReport = async () => {
    if (!reportText.trim()) { toast.error('Please enter report text'); return; }
    setSendingReport(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await timesheetAPI.sendReport(today, reportText);
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  if (loading && !weekData) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const weekDates = weekData ? getWeekDates(weekData.week_start) : [];
  const StatusBadge = STATUS_BADGES[weekData?.status || 'draft'];

  return (
    <div data-testid="timesheet-page" className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">My Timesheet</h1>
          <p className="text-muted-foreground mt-1">Log your daily work hours and tasks</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigateWeek(-7)} data-testid="prev-week-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-lg">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">
              {weekData ? `${formatDate(weekData.week_start)} - ${formatDate(weekData.week_end)}` : 'Loading...'}
            </span>
          </div>
          <Button variant="outline" size="icon" onClick={() => navigateWeek(7)} data-testid="next-week-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Status & Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Badge className={StatusBadge?.color}>
            {StatusBadge?.icon && <StatusBadge.icon className="h-3 w-3 mr-1" />}
            {weekData?.status?.charAt(0).toUpperCase() + weekData?.status?.slice(1)}
          </Badge>
          <span className="text-sm text-muted-foreground">
            Total: <strong>{weekData?.week_total_hours?.toFixed(1) || 0}h</strong>
            {' | '}
            Billable: <strong>{weekData?.week_billable_hours?.toFixed(1) || 0}h</strong>
          </span>
        </div>
        <div className="flex gap-2">
          {weekData?.can_edit && (
            <>
              <Button onClick={syncFromDevOps} variant="outline" disabled={syncing} data-testid="sync-devops-btn">
                {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                Sync from DevOps
              </Button>
              <Button onClick={() => openAddEntry()} variant="outline" data-testid="add-entry-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Entry
              </Button>
            </>
          )}
          {weekData?.can_submit && weekData?.entries?.length > 0 && (
            <Button onClick={submitWeek} disabled={saving} data-testid="submit-week-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit Week
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="timesheet">Timesheet</TabsTrigger>
          <TabsTrigger value="ai-summary">AI Summary</TabsTrigger>
        </TabsList>

        <TabsContent value="timesheet">
          <TimesheetWeeklyGrid
            weekData={weekData} weekDates={weekDates} projects={projects}
            onEditEntry={openEditEntry} onAddEntry={openAddEntry}
            onDeleteEntry={deleteEntry} onSelectDevOpsTask={selectDevOpsTask}
          />
        </TabsContent>

        <TabsContent value="ai-summary">
          <TimesheetAISummary
            aiSummary={aiSummary} aiLoading={aiLoading}
            reportText={reportText} sendingReport={sendingReport}
            onGenerateSummary={generateAISummary} onSetReportText={setReportText}
            onSendReport={sendReport}
          />
        </TabsContent>
      </Tabs>

      {/* Entry Dialog */}
      <TimesheetEntryDialog
        open={entryDialogOpen} onOpenChange={setEntryDialogOpen}
        editingEntry={editingEntry} entryForm={entryForm} setEntryForm={setEntryForm}
        onSave={saveEntry} saving={saving} projects={projects} weekData={weekData}
      />
    </div>
  );
}
